import type { UseFormMethods } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { emailMustMatch, emailRegex, noSpacesRegex } from "@utils/formControls";
import { ICustomerDetailsForm } from "@features/customerDetails/ICustomerDetailsForm";
import { keycloakUtils } from "itsf-ui-common";
import { Grid } from "@mui/material";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";

interface IParams {
    emailError?: string;
    formMethods: UseFormMethods<ICustomerDetailsForm>;
}

export const CustomerDetailsEmailFields = (props: IParams) => {
    const { formMethods, emailError } = props;
    const { t } = useTranslation(["customerDetails", "validation"]);

    const isAuthenticated = keycloakUtils.isAuthenticated();
    const email = useWatch<string>({ name: "email", control: formMethods.control });

    return (
        <>
            <Grid item md={6} xs={12}>
                <Controller
                    control={formMethods.control}
                    name="email"
                    render={(renderProps) => (
                        <LabeledTextField
                            aria-describedby="emailHelper"
                            disabled={isAuthenticated}
                            error={Boolean(formMethods.errors.email) || Boolean(emailError)}
                            fullWidth
                            helperText={
                                formMethods.errors.email?.message ?? emailError ?? t("customerDetails:emailHelper")
                            }
                            id="email"
                            inputRef={renderProps.ref}
                            isReadOnly={isAuthenticated}
                            isRequired
                            labelText={t("customerDetails:emailAddress")}
                            name={renderProps.name}
                            placeholder={t("customerDetails:emailAddressPlaceholder")}
                            value={renderProps.value}
                            onChange={renderProps.onChange}
                        />
                    )}
                    rules={{
                        required: t("validation:required"),
                        validate: (value: string) => noSpacesRegex(value) ?? emailRegex(value),
                    }}
                />
            </Grid>
            <Grid item md={6} xs={12}>
                {!isAuthenticated && (
                    <Controller
                        control={formMethods.control}
                        name="emailConfirm"
                        render={(renderProps) => (
                            <LabeledTextField
                                aria-describedby="emailConfirmHelper"
                                error={Boolean(formMethods.errors.emailConfirm)}
                                fullWidth
                                helperText={formMethods.errors.emailConfirm?.message}
                                id="email-confirm"
                                inputRef={renderProps.ref}
                                isRequired
                                labelText={t("customerDetails:confirmEmail")}
                                name={renderProps.name}
                                placeholder={t("customerDetails:emailAddressPlaceholder")}
                                value={renderProps.value}
                                onChange={renderProps.onChange}
                                onPaste={(event) => event.preventDefault()}
                            />
                        )}
                        rules={{
                            required: t("validation:required"),
                            validate: (value: string) =>
                                noSpacesRegex(value) ?? emailRegex(value) ?? emailMustMatch(value, email),
                        }}
                    />
                )}
            </Grid>
        </>
    );
};
